import { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import { ChevronLeft, ChevronRight, Download } from 'lucide-react'
import ReportPageHeader from '@/components/reports/ReportPageHeader'
import { helpdeskReportService, type ReportFilters } from '@/lib/services/helpdeskReportService'

const PER_PAGE = 25

type AttendanceRow = Record<string, string | number | null>

function exportRowsToCsv(rows: AttendanceRow[], filename: string) {
  if (!rows.length) return
  const cols: Array<{ key: string; header: string }> = [
    { key: 'fe_name', header: 'FE Name' },
    { key: 'punch_in_at', header: 'Punch In' },
    { key: 'punch_out_at', header: 'Punch Out' },
    { key: 'hours_worked', header: 'Hours Worked' },
    { key: 'status', header: 'Status' },
  ]
  const lines = [
    cols.map((c) => c.header).join(','),
    ...rows.map((row) =>
      cols.map((c) => `"${String(row[c.key] ?? '').replace(/"/g, '""')}"`).join(','),
    ),
  ]
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export default function FeAttendancePage() {
  const [filters, setFilters] = useState<ReportFilters>({})
  const [feUserIdInput, setFeUserIdInput] = useState('')
  const [page, setPage] = useState(1)
  const [rows, setRows] = useState<AttendanceRow[]>([])
  const [meta, setMeta] = useState<{
    total: number
    per_page: number
    current_page: number
    last_page: number
  } | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(() => {
    setLoading(true)
    const effectiveFilters: ReportFilters = {
      ...filters,
      ...(feUserIdInput.trim() ? { fe_user_id: feUserIdInput.trim() } : {}),
    }
    helpdeskReportService
      .getFeAttendance(effectiveFilters, page, PER_PAGE)
      .then((res) => {
        setRows(res.data ?? [])
        if (res.meta) setMeta(res.meta)
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [filters, feUserIdInput, page])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleFiltersChange = (next: ReportFilters) => {
    setFilters(next)
    setPage(1)
  }

  const handleFeUserIdChange = (val: string) => {
    setFeUserIdInput(val)
    setPage(1)
  }

  return (
    <Box p={4}>
      <ReportPageHeader
        title="FE Attendance Report"
        subtitle="Field engineer punch-in/out records"
        reportType="fe-attendance"
        filters={filters}
        onFiltersChange={handleFiltersChange}
      />

      <HStack mb={4} spacing={3} flexWrap="wrap" align="flex-end">
        <FormControl maxW="160px">
          <FormLabel fontSize="xs">FE User ID</FormLabel>
          <Input
            type="number"
            size="sm"
            min={1}
            placeholder="All FEs"
            value={feUserIdInput}
            onChange={(e) => handleFeUserIdChange(e.target.value)}
          />
        </FormControl>
        <Button
          size="sm"
          variant="outline"
          leftIcon={<Download size={14} />}
          onClick={() => exportRowsToCsv(rows, `fe-attendance-p${page}.csv`)}
          isDisabled={rows.length === 0 || loading}
        >
          Export CSV
        </Button>
      </HStack>

      <Card>
        <CardBody overflowX="auto">
          {loading ? (
            <Skeleton height="240px" />
          ) : (
            <>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>FE Name</Th>
                    <Th>Punch In</Th>
                    <Th>Punch Out</Th>
                    <Th>Hours</Th>
                    <Th>Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {rows.length === 0 ? (
                    <Tr>
                      <Td colSpan={5} textAlign="center" color="gray.400" py={8} fontSize="sm">
                        No records found
                      </Td>
                    </Tr>
                  ) : (
                    rows.map((row, i) => (
                      <Tr key={String(row.id ?? i)}>
                        <Td>{row.fe_name}</Td>
                        <Td>{row.punch_in_at}</Td>
                        <Td>{row.punch_out_at ?? '—'}</Td>
                        <Td>{row.hours_worked ?? '—'}</Td>
                        <Td>{row.status}</Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>

              {meta && meta.last_page > 1 && (
                <HStack mt={4} justify="space-between" px={2}>
                  <Text fontSize="sm" color="gray.500">
                    Page {meta.current_page} of {meta.last_page} &bull; {meta.total} total records
                  </Text>
                  <HStack spacing={2}>
                    <Button
                      size="xs"
                      variant="outline"
                      leftIcon={<ChevronLeft size={12} />}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      isDisabled={page <= 1}
                    >
                      Prev
                    </Button>
                    <Button
                      size="xs"
                      variant="outline"
                      rightIcon={<ChevronRight size={12} />}
                      onClick={() => setPage((p) => Math.min(meta.last_page, p + 1))}
                      isDisabled={page >= meta.last_page}
                    >
                      Next
                    </Button>
                  </HStack>
                </HStack>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </Box>
  )
}
